import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    servicio: '',
    mensaje: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.mensaje.length > 500) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6gscf2u8ojn6g516jqg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ nombre: '', email: '', telefono: '', servicio: '', mensaje: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contacto" className="relative py-16 md:py-32 px-4 md:px-8 bg-[#0A0A0A] overflow-hidden">
      {/* Marca de agua decorativa — solo desktop */}
      <div className="absolute bottom-0 left-0 right-0 overflow-hidden pointer-events-none hidden md:block">
        <div className="text-[#1A1A1A] font-black text-[180px] leading-none whitespace-nowrap select-none" style={{ fontFamily: 'Poppins, sans-serif' }}>
          ELECTRICIDAD
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Grid superior */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-16 mb-12 md:mb-20">
          {/* Columna 1 - CTA Principal */}
          <div className="col-span-2 md:col-span-1">
            <h3 className="text-white font-bold text-xl md:text-3xl leading-tight mb-3 md:mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              ¿Hablamos de tu proyecto?
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
              Te asesoramos sin compromiso. Cuéntanos qué necesitas y te damos la mejor solución eléctrica o de iluminación.
            </p>
          </div>

          {/* Columna 2 - Navegación */}
          <div>
            <h4 className="text-white font-medium text-base md:text-lg mb-4 md:mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Navegación
            </h4>
            <ul className="space-y-2 md:space-y-4">
              {[
                { label: 'Inicio', href: '#inicio' },
                { label: 'Nosotros', href: '#nosotros' },
                { label: 'Servicios', href: '#servicios' },
                { label: 'Iluminación LED', href: '#iluminacion' },
                { label: 'Contacto', href: '#contacto' },
              ].map((item) => (
                <li key={item.label}>
                  <a
                    href={item.href}
                    className="text-gray-400 hover:text-[#FFD700] transition-colors duration-300 text-sm cursor-pointer"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Columna 3 - Redes Sociales */}
          <div>
            <h4 className="text-white font-medium text-base md:text-lg mb-4 md:mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Síguenos
            </h4>
            <div className="space-y-3 md:space-y-4">
              {[
                { name: 'Instagram', icon: 'ri-instagram-line', url: '#' },
                { name: 'YouTube', icon: 'ri-youtube-line', url: '#' },
                { name: 'Facebook', icon: 'ri-facebook-line', url: '#' }
              ].map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  rel="nofollow"
                  className="flex items-center gap-3 text-gray-400 hover:text-[#FFD700] transition-colors duration-300 cursor-pointer"
                >
                  <div className="w-7 h-7 md:w-8 md:h-8 bg-[#FFD700] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className={`${social.icon} text-black text-base md:text-lg`}></i>
                  </div>
                  <span className="text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>{social.name}</span>
                </a>
              ))}
            </div>
          </div>

          {/* Columna 4 - Newsletter */}
          <div className="col-span-2 md:col-span-1">
            <h4 className="text-white font-medium text-base md:text-lg mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Novedades y ofertas
            </h4>
            <p className="text-gray-400 text-sm mb-4 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
              Recibe consejos de mantenimiento eléctrico y ofertas exclusivas
            </p>
            <div className="flex gap-2">
              <input
                type="email"
                placeholder="tu@email.com"
                className="flex-1 bg-[#1A1A1A] border border-[#FFD700]/30 text-white px-3 md:px-4 py-3 rounded-xl focus:outline-none focus:border-[#FFD700] transition-colors duration-300 text-sm min-w-0"
                style={{ fontFamily: 'Inter, sans-serif' }}
              />
              <button className="bg-[#FFD700] w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center flex-shrink-0 hover:bg-[#FFC700] hover:shadow-lg hover:shadow-[#FFD700]/50 transition-all duration-300 hover:scale-105 cursor-pointer">
                <i className="ri-arrow-right-line text-black text-xl"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Formulario de Contacto */}
        <div className="pt-10 md:pt-16 border-t border-gray-800">
          <div className="text-center mb-8 md:mb-12">
            <h3 className="text-white font-bold text-2xl md:text-4xl mb-2 md:mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Solicita tu Presupuesto
            </h3>
            <p className="text-gray-400 text-sm md:text-lg" style={{ fontFamily: 'Inter, sans-serif' }}>
              Respuesta garantizada en menos de 24 horas
            </p>
          </div>

          <form id="presupuesto-form" data-readdy-form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
              <div>
                <label htmlFor="nombre" className="block text-gray-400 text-sm mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Nombre completo *
                </label>
                <input
                  type="text"
                  id="nombre"
                  name="nombre"
                  required
                  value={formData.nombre}
                  onChange={handleChange}
                  className="w-full bg-[#1A1A1A] border border-gray-700 text-white px-4 py-3 rounded-xl focus:outline-none focus:border-[#FFD700] transition-colors duration-300 text-sm"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-gray-400 text-sm mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full bg-[#1A1A1A] border border-gray-700 text-white px-4 py-3 rounded-xl focus:outline-none focus:border-[#FFD700] transition-colors duration-300 text-sm"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                />
              </div>

              <div>
                <label htmlFor="telefono" className="block text-gray-400 text-sm mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Teléfono *
                </label>
                <input
                  type="tel"
                  id="telefono"
                  name="telefono"
                  required
                  value={formData.telefono}
                  onChange={handleChange}
                  className="w-full bg-[#1A1A1A] border border-gray-700 text-white px-4 py-3 rounded-xl focus:outline-none focus:border-[#FFD700] transition-colors duration-300 text-sm"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                />
              </div>

              <div>
                <label htmlFor="servicio" className="block text-gray-400 text-sm mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Servicio de interés *
                </label>
                <select
                  id="servicio"
                  name="servicio"
                  required
                  value={formData.servicio}
                  onChange={handleChange}
                  className="w-full bg-[#1A1A1A] border border-gray-700 text-white px-4 py-3 rounded-xl focus:outline-none focus:border-[#FFD700] transition-colors duration-300 cursor-pointer text-sm"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <option value="">Selecciona un servicio</option>
                  <option value="instalaciones">Instalaciones Eléctricas</option>
                  <option value="reparaciones">Reparaciones Urgentes</option>
                  <option value="iluminacion-led">Diseño Iluminación LED</option>
                  <option value="tiras-led">Tiras LED Ambientales</option>
                  <option value="focos-empotrables">Focos Empotrables</option>
                  <option value="cargadores">Cargadores Vehículo Eléctrico</option>
                  <option value="seguridad">Seguridad &amp; Cámaras</option>
                  <option value="certificaciones">Certificaciones Oficiales</option>
                  <option value="asesoria">Asesoramiento General</option>
                </select>
              </div>
            </div>

            <div className="mb-4 md:mb-6">
              <label htmlFor="mensaje" className="block text-gray-400 text-sm mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                Cuéntanos tu proyecto *
              </label>
              <textarea
                id="mensaje"
                name="mensaje"
                required
                rows={5}
                maxLength={500}
                value={formData.mensaje}
                onChange={handleChange}
                className="w-full bg-[#1A1A1A] border border-gray-700 text-white px-4 py-3 rounded-xl focus:outline-none focus:border-[#FFD700] transition-colors duration-300 resize-none text-sm"
                style={{ fontFamily: 'Inter, sans-serif' }}
                placeholder="Describe tu proyecto o problema eléctrico... (máximo 500 caracteres)"
              ></textarea>
              <div className={`text-xs mt-1 text-right ${formData.mensaje.length > 500 ? 'text-red-400' : 'text-gray-500'}`}>
                {formData.mensaje.length}/500 caracteres
              </div>
            </div>

            <div className="text-center">
              <button
                type="submit"
                disabled={isSubmitting || formData.mensaje.length > 500}
                className="w-full md:w-auto bg-[#FFD700] text-black px-8 md:px-12 py-3 md:py-4 rounded-full font-semibold text-base md:text-lg hover:bg-[#FFC700] hover:shadow-2xl hover:shadow-[#FFD700]/50 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {isSubmitting ? 'Enviando...' : 'Enviar Solicitud'}
              </button>
            </div>

            {submitStatus === 'success' && (
              <div className="mt-6 p-4 bg-green-500/20 border border-green-500 rounded-xl text-green-400 text-center text-sm">
                ¡Gracias! Hemos recibido tu solicitud. Te contactaremos en menos de 24h.
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="mt-6 p-4 bg-red-500/20 border border-red-500 rounded-xl text-red-400 text-center text-sm">
                Hubo un error al enviar. Por favor, inténtalo de nuevo.
              </div>
            )}
          </form>

          {/* Información de contacto */}
          <div className="mt-10 md:mt-16 grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8 text-center">
            <div>
              <div className="w-14 h-14 md:w-16 md:h-16 bg-[#FFD700]/20 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                <i className="ri-phone-line text-[#FFD700] text-xl md:text-2xl"></i>
              </div>
              <h4 className="text-white font-semibold text-base md:text-lg mb-1 md:mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>Urgencias 24/7</h4>
              <a href="tel:+34633518361" className="text-gray-400 hover:text-[#FFD700] transition-colors duration-300 text-sm md:text-base" style={{ fontFamily: 'Inter, sans-serif' }}>+34 633 518 361</a>
            </div>
            <div>
              <div className="w-14 h-14 md:w-16 md:h-16 bg-[#FFD700]/20 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                <i className="ri-whatsapp-line text-[#FFD700] text-xl md:text-2xl"></i>
              </div>
              <h4 className="text-white font-semibold text-base md:text-lg mb-1 md:mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>WhatsApp</h4>
              <a href="https://wa.me/34633518361" rel="nofollow" className="text-gray-400 hover:text-[#FFD700] transition-colors duration-300 cursor-pointer text-sm md:text-base" style={{ fontFamily: 'Inter, sans-serif' }}>
                Enviar mensaje
              </a>
            </div>
            <div>
              <div className="w-14 h-14 md:w-16 md:h-16 bg-[#FFD700]/20 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                <i className="ri-mail-line text-[#FFD700] text-xl md:text-2xl"></i>
              </div>
              <h4 className="text-white font-semibold text-base md:text-lg mb-1 md:mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>Email</h4>
              <a href="mailto:rachidabdolcadirmdq@gmail.com" className="text-gray-400 hover:text-[#FFD700] transition-colors duration-300 text-xs md:text-sm break-all" style={{ fontFamily: 'Inter, sans-serif' }}>rachidabdolcadirmdq@gmail.com</a>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-10 md:mt-16 pt-6 md:pt-8 border-t border-gray-800 text-center">
          <p className="text-gray-500 text-xs md:text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
            © 2025 Electricidad y Tecnología. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </section>
  );
}
